import type { ActionResponse, ClientGameState } from '../shared/types';
import { api, showError } from './api';
import { hideRoundOverlay, isShowingFinishedTrick, renderGame, renderTrickSlot, resetRenderCache } from './render';
import {
  apply,
  bidInFlight,
  currentGameId,
  getAppliedSeq,
  getState,
  paintLocal,
  playInFlight,
  playerId,
  resetStore,
  setBidInFlight,
  setGame,
  setPlayInFlight,
  viewerSeat,
} from './store';
import { disconnect, rebuildNow, report } from './sse';

type GameSnapshot = {
  seq: number;
  gameState: ClientGameState;
};

/**
 * Loopback shows the server answering a move in ~10ms; a request still open
 * after this long is sitting behind a dead socket, and waiting on it only
 * lengthens the freeze. Recover instead.
 */
const ACTION_TIMEOUT_MS = 3500;
const RECOVER_TIMEOUT_MS = 3000;

function cloneState(state: ClientGameState): ClientGameState {
  return structuredClone(state);
}

export function leaveGame(): void {
  disconnect();
  resetStore();
  setGame(null, 0);
  resetRenderCache();
  hideRoundOverlay();
}

function withTimeout<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`${what} timed out — try again`)), ms);
    promise.then(
      value => { window.clearTimeout(timer); resolve(value); },
      err => { window.clearTimeout(timer); reject(err); },
    );
  });
}

/**
 * The action request died. The move may still have landed, so fetch the board
 * once and rebuild the stream: the socket the request was on is the same one
 * the stream shares, so it is not to be trusted either. Returns whether the
 * board moved past `seqBefore`.
 */
async function recoverAfter(what: string, seqBefore: number, reason: string): Promise<boolean> {
  if (!currentGameId || !playerId) return false;
  report(`${what}-recover`, reason);
  rebuildNow();
  try {
    const snap = await withTimeout(
      api<GameSnapshot>('GET', `/game/${currentGameId}?playerId=${playerId}`),
      RECOVER_TIMEOUT_MS,
      'Recovery',
    );
    apply({ seq: snap.seq, gameState: snap.gameState, events: [] });
  } catch { /* the rebuilt stream delivers the same snapshot on open */ }
  return getAppliedSeq() > seqBefore;
}

export async function handleBid(bid: number): Promise<void> {
  if (!currentGameId || !playerId || bidInFlight) return;
  const seqAtClick = getAppliedSeq();
  setBidInFlight(true);
  renderGame();
  try {
    const data = await withTimeout(
      api<ActionResponse>('POST', `/game/${currentGameId}/bid`, { bid, playerId }),
      ACTION_TIMEOUT_MS,
      'Bid',
    );
    apply({ seq: data.seq, gameState: data.gameState, events: data.events ?? [] });
  } catch (e) {
    // The stream may already carry the bid; only complain if nothing moved.
    const moved = getAppliedSeq() > seqAtClick
      || await recoverAfter('bid', seqAtClick, (e as Error).message);
    if (!moved) showError((e as Error).message);
  } finally {
    setBidInFlight(false);
    renderGame();
  }
}

export async function handlePlayCard(cardIndex: number): Promise<void> {
  const gameState = getState();
  if (!currentGameId || !playerId || playInFlight || !gameState) return;
  if (isShowingFinishedTrick()) return;
  if (!gameState.validCardIndices.includes(cardIndex)) return;

  const card = gameState.humanHand[cardIndex];
  if (!card) return;
  const snapshot = cloneState(gameState);
  const seqAtClick = getAppliedSeq();

  // Show the card on the table the moment it is clicked. The server snapshot
  // replaces this; if the request fails we put the hand back.
  const local = cloneState(gameState);
  local.humanHand = local.humanHand.filter((_, i) => i !== cardIndex);
  local.validCardIndices = [];
  local.currentTrick = [...(local.currentTrick ?? [])];
  while (local.currentTrick.length < 4) local.currentTrick.push(null);
  local.currentTrick[viewerSeat] = card;
  paintLocal(local);
  setPlayInFlight(true);
  renderTrickSlot(viewerSeat, card);
  renderGame();

  try {
    const data = await withTimeout(
      api<ActionResponse>('POST', `/game/${currentGameId}/play`, { cardIndex, playerId }),
      ACTION_TIMEOUT_MS,
      'Play',
    );
    apply({ seq: data.seq, gameState: data.gameState, events: data.events ?? [] });
  } catch (e) {
    // SSE may already have the play. Rolling back would erase a good board.
    const moved = getAppliedSeq() > seqAtClick
      || await recoverAfter('play', seqAtClick, (e as Error).message);
    if (!moved && getAppliedSeq() <= seqAtClick) {
      paintLocal(snapshot);
      showError((e as Error).message);
    }
  } finally {
    setPlayInFlight(false);
    renderGame();
  }
}

export async function handleNextRound(): Promise<void> {
  if (!currentGameId || !playerId) return;
  const seqAtClick = getAppliedSeq();
  try {
    hideRoundOverlay();
    const data = await withTimeout(
      api<ActionResponse>('POST', `/game/${currentGameId}/next-round`, { playerId }),
      ACTION_TIMEOUT_MS,
      'Deal',
    );
    apply({ seq: data.seq, gameState: data.gameState, events: data.events ?? [] });
    resetRenderCache();
  } catch (e) {
    const moved = getAppliedSeq() > seqAtClick
      || await recoverAfter('deal', seqAtClick, (e as Error).message);
    if (!moved) showError((e as Error).message);
  } finally {
    // The overlay was hidden on click. If the deal did not go through, this
    // paints it back rather than leaving a bare board with nothing to press.
    renderGame();
  }
}

export { viewerSeat };
